import React, { useState } from "react";

import {
  AUTH_COPY,
  MAXIMUM_EMAIL_LENGTH,
} from "./authenticationConstants";
import { isValidEmail, normalizeEmail } from "./authenticationPayload";
import "./treatmentAuthentication.css";

/**
 * The first screen of every Program intake.
 *
 * Email only. Name, phone, date of birth, sex, and consent are deliberately
 * absent until the lookup has resolved which branch the patient is on.
 */
export function EmailLookupStep({
  email: initialEmail = "",
  brandName = "WellieMD",
  pending = false,
  unavailable = false,
  onSubmit,
  onRetry,
}) {
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState(null);
  const displayBrandName = brandName?.trim() || "WellieMD";

  const handleSubmit = (event) => {
    event.preventDefault();
    if (pending) return; // Guards duplicate clicks while the lookup is in flight.
    const value = normalizeEmail(email);
    if (!value) {
      setError(AUTH_COPY.errors.emailRequired);
      return;
    }
    if (!isValidEmail(value)) {
      setError(AUTH_COPY.errors.emailInvalid);
      return;
    }
    setError(null);
    onSubmit(value);
  };

  return (
    <form className="treatment-auth" onSubmit={handleSubmit} noValidate>
      <p className="treatment-auth__eyebrow">
        {AUTH_COPY.emailStep.eyebrowPrefix} {displayBrandName}
      </p>
      <h1 className="treatment-auth__title">{AUTH_COPY.emailStep.title}</h1>
      <p className="treatment-auth__description">
        {AUTH_COPY.emailStep.description}
      </p>

      <label className="treatment-auth__label" htmlFor="intake-email">
        {AUTH_COPY.emailStep.emailLabel}
        <span className="treatment-auth__required" aria-hidden="true">
          *
        </span>
      </label>
      <input
        id="intake-email"
        name="email"
        type="email"
        autoComplete="email"
        inputMode="email"
        className={`treatment-auth__input${error ? " treatment-auth__input--error" : ""}`}
        placeholder={AUTH_COPY.emailStep.emailPlaceholder}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "intake-email-error" : "intake-email-note"}
        disabled={pending}
        maxLength={MAXIMUM_EMAIL_LENGTH}
      />
      {error ? (
        <p className="treatment-auth__error" id="intake-email-error" role="alert">
          {error}
        </p>
      ) : (
        <p className="treatment-auth__note" id="intake-email-note">
          {AUTH_COPY.emailStep.consentNotePrefix} {displayBrandName}{" "}
          {AUTH_COPY.emailStep.consentNoteSuffix}
        </p>
      )}

      {unavailable && (
        <div className="treatment-auth__banner" role="alert">
          <p>{AUTH_COPY.errors.lookupUnavailable}</p>
          <button
            type="button"
            className="treatment-auth__link"
            onClick={onRetry}
          >
            {AUTH_COPY.errors.retry}
          </button>
        </div>
      )}

      <button
        type="submit"
        className="treatment-auth__submit"
        disabled={pending}
      >
        {pending ? AUTH_COPY.emailStep.submitting : AUTH_COPY.emailStep.submit}
      </button>
    </form>
  );
}

export default EmailLookupStep;
